/**
 * Calendar dates as the mosque writes them: 'YYYY-MM-DD', a day and nothing else.  MKK adoption Batch 1.
 *
 * A due date or a planner date is a DAY in the mosque's own calendar, not an instant. Kept as text and computed with
 * UTC arithmetic on that text, it never moves when the server, the browser or the test runs in another time zone — the
 * class of bug where "today" is the UTC date and a late-evening entry in Oslo lands on the wrong day. "Today" itself
 * comes from tz.dateIn(now, mosque.timezone); everything after that is these functions.
 *
 * Pure: no clock, no time zone.
 */
const RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** The parts of a valid date, or null: a real calendar day between 2000 and 2199 (no 2026-02-30, no year 0). */
function parse(s) {
  const m = typeof s === 'string' ? RE.exec(s) : null;
  if (!m) return null;
  const y = Number(m[1]), mo = Number(m[2]), d = Number(m[3]);
  if (y < 2000 || y > 2199 || mo < 1 || mo > 12 || d < 1 || d > daysInMonth(y, mo)) return null;
  return { y, m: mo, d };
}
const valid = (s) => parse(s) !== null;
const pad = (n) => String(n).padStart(2, '0');
const fmt = (y, m, d) => `${y}-${pad(m)}-${pad(d)}`;
/** Days in month m (1–12) of year y. */
function daysInMonth(y, m) { return new Date(Date.UTC(y, m, 0)).getUTCDate(); }
const toUtc = (s) => { const p = parse(s); return Date.UTC(p.y, p.m - 1, p.d); };
const fromUtc = (ms) => { const d = new Date(ms); return fmt(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate()); };
/** s plus n days (n may be negative). */
function add(s, n) { return fromUtc(toUtc(s) + n * 86400000); }
/** Whole days from a to b (b − a). */
function diff(a, b) { return Math.round((toUtc(b) - toUtc(a)) / 86400000); }
/** 0 = Sunday … 6 = Saturday. */
function weekday(s) { return new Date(toUtc(s)).getUTCDay(); }
/** Year and month (1–12) plus k months, as { y, m }. */
function addMonths(y, m, k) { const t = (y * 12 + (m - 1)) + k; return { y: Math.floor(t / 12), m: (t % 12) + 1 }; }
/**
 * An export's period from a query (Utlegg, Kontantgaver): both days valid, from ≤ to; the default is this year (from
 * `today`, the mosque's day) up to today. → { ok, value: { from, to } } | { ok: false, message }
 */
function period(q = {}, today) {
  const from = q.from ? String(q.from) : `${String(today).slice(0, 4)}-01-01`;
  const to = q.to ? String(q.to) : today;
  if (!valid(from) || !valid(to)) return { ok: false, message: 'Oppgi perioden som to datoer (ÅÅÅÅ-MM-DD).' };
  if (diff(from, to) < 0) return { ok: false, message: 'Perioden slutter før den begynner.' };
  return { ok: true, value: { from, to } };
}

module.exports = { parse, valid, fmt, daysInMonth, add, diff, weekday, addMonths, period };
